/**
 * Plyr Auto-Initialization
 * 
 * Automatically initializes Plyr on video/audio elements in rich text content
 * and other dynamically loaded areas that don't have the Stimulus controller.
 */

import Plyr from 'plyr';
import 'plyr/dist/plyr.css';
import {
  installPlyrPrototypeCaptionsFix,
  nudgeYouTubeCaptionsIfNeeded,
  shimCaptionsLanguagesIfNeeded,
  wireYouTubeEmbedCaptionToggle,
  withProviderEmbedCaptions,
} from './plyr_embed_caption_options';

installPlyrPrototypeCaptionsFix();

// Content selectors where media elements should be auto-initialized
const CONTENT_SELECTORS = [
  '.post-content',
  '.new-post-content',
  '.academy-post-rich-content',
  '.rich-text-content',
  '[data-rich-text]',
  '.comment-text',
  '.comment-container',
  '.comment-details'
];

// Default Plyr options for content media
const DEFAULT_OPTIONS = {
  controls: [
    'play-large',
    'play',
    'progress',
    'current-time',
    'mute',
    'volume',
    'captions',
    'settings',
    'fullscreen'
  ],
  settings: ['captions', 'quality', 'speed', 'loop'],
  tooltips: { controls: true, seek: true },
  keyboard: { focused: true, global: false },
  captions: { active: false, language: 'auto', update: false },
  fullscreen: { enabled: true, fallback: true, iosNative: false },
  speed: { selected: 1, options: [0.5, 0.75, 1, 1.25, 1.5, 2] },
  clickToPlay: true,
  hideControls: true,
  resetOnEnd: false,
  seekTime: 10,
  volume: 1,
  storage: { enabled: true, key: 'plyr' }
};

/**
 * Check if an element already has Plyr initialized
 */
function hasPlyrInitialized(element) {
  return element._plyrInstance || element.closest('.plyr');
}

/**
 * Check if element is within a Stimulus plyr controller scope
 */
function isInPlyrControllerScope(element) {
  return element.closest('[data-controller~="plyr"]');
}

function isInsideEditor(element) {
  return element.closest('.ProseMirror') || element.closest('[contenteditable="true"]');
}

/**
 * Skip video/audio inside the resource picker modal (preview only, no Plyr)
 */
function isInsideResourceModal(element) {
  return element.closest('.resource-modal') || element.closest('.resource-item');
}

/**
 * Initialize Plyr on a media element
 */
function initializePlyr(element) {
  try {
    // If this is a provider embed container, clear editor placeholder content first.
    // (The editor node view inserts a message inside the container.)
    if (element && element.classList && element.classList.contains('plyr__video-embed')) {
      element.innerHTML = '';
    }
    const plyrOptions = withProviderEmbedCaptions(DEFAULT_OPTIONS, element);
    const player = new Plyr(element, plyrOptions);
    shimCaptionsLanguagesIfNeeded(player);
    nudgeYouTubeCaptionsIfNeeded(player);
    wireYouTubeEmbedCaptionToggle(player);
    element._plyrInstance = player;

    player.on('ready', () => {
      console.debug('[PlyrAutoInit] Player ready:', element.tagName);
    });
    
    return player;
  } catch (error) {
    console.error('[PlyrAutoInit] Failed to initialize:', error);
    return null;
  }
}

/**
 * Find and initialize all uninitialized media elements in content areas
 */
function initializeContentMedia() {
  const contentContainers = document.querySelectorAll(CONTENT_SELECTORS.join(', '));
  
  contentContainers.forEach((container) => {
    const mediaElements = container.querySelectorAll('video, audio, .plyr__video-embed,[data-plyr-provider][data-plyr-embed-id]');
    
    mediaElements.forEach((element) => {
      // Skip if already initialized or within a Stimulus controller
      if (hasPlyrInitialized(element) || isInPlyrControllerScope(element)) {
        return;
      }
      // Skip videos/audio inside the post editor (ProseMirror) - avoids PiP metadata error and too many WebMediaPlayers
      if (isInsideEditor(element)) {
        return;
      }
      // Skip videos/audio inside the resource picker modal (preview only, no controls)
      if (isInsideResourceModal(element)) {
        return;
      }

      initializePlyr(element);
    });
  });
}

/**
 * Set up MutationObserver to watch for dynamically added media elements
 */
function setupMutationObserver() {
  const observer = new MutationObserver((mutations) => {
    let hasNewMedia = false;
    
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        
        // Check if the added node is a media element or contains media elements
        if (
          node.matches &&
          (node.matches('video, audio, .plyr__video-embed,[data-plyr-provider][data-plyr-embed-id]') ||
            node.querySelector('video, audio, .plyr__video-embed,[data-plyr-provider][data-plyr-embed-id]'))
        ) {
          hasNewMedia = true;
        }
      });
    });
    
    if (hasNewMedia) {
      // Debounce initialization
      setTimeout(initializeContentMedia, 100);
    }
  });
  
  // Observe the document body for changes
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
  
  return observer;
}

// Initialize on page load
function initialize() {
  initializeContentMedia();
  setupMutationObserver();
}

// Run on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initialize);
} else {
  initialize();
}

// Re-initialize on Turbo page loads
document.addEventListener('turbo:load', initializeContentMedia);

// Also handle Turbo frame updates
document.addEventListener('turbo:frame-load', (event) => {
  const frame = event.target;
  const mediaElements = frame.querySelectorAll('video, audio, .plyr__video-embed,[data-plyr-provider][data-plyr-embed-id]');
  
  mediaElements.forEach((element) => {
    if (!hasPlyrInitialized(element) && !isInPlyrControllerScope(element) && !isInsideEditor(element) && !isInsideResourceModal(element)) {
      initializePlyr(element);
    }
  });
});

// Export for potential external use
export { initializeContentMedia, initializePlyr };
